// src/pages/SupportChats.js — MIDTC Chats live-chat agent console.
// Two-pane console over the /chat-admin/* endpoints: a session list on the left,
// a live transcript + composer + agent actions on the right, plus an admin-only
// Sites section for issuing widget keys.
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare, RefreshCw, Ticket as TicketIcon, Bot, User, Headphones,
  ChevronDown, ChevronRight, Copy, Check, AlertTriangle, Send, X, Plus, KeyRound,
  Download,
} from "lucide-react";
import { sx, tokens } from "../theme";
import { useAuth } from "../context/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import {
  getChatSessions, getChatMessages, takeoverChat, releaseChat, sendChatMessage,
  closeChat, escalateChat, getChatStats,
  getChatSites, createChatSite, rotateChatSiteKey, updateChatSite, getClients,
} from "../api";

// Attachments are served from the backend origin (not under /api), so links must
// be prefixed with the bare origin, e.g. https://ticketing.midtc.com/uploads/chat/…
const ORIGIN = process.env.REACT_APP_API_URL || "https://ticketing.midtc.com";

const fmtTime = (d) => d ? new Date(d).toLocaleString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "";
const fmtRel  = (d) => {
  if (!d) return "";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 45)   return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);          return `${days}d ago`;
};

const errText = (e) => e?.response?.data?.error || e?.message || "Something went wrong";

// Session status → pill styling. Hues match the app's badge palette.
const STATUS_META = {
  bot:    { label:"Bot",    color:"var(--badge-blue)",   bg:"rgba(96,165,250,0.15)",  border:"rgba(96,165,250,0.35)" },
  intake: { label:"Intake", color:"var(--badge-amber)",  bg:"rgba(234,179,8,0.15)",   border:"rgba(234,179,8,0.35)" },
  agent:  { label:"Agent",  color:"var(--badge-green)",  bg:"rgba(52,211,153,0.15)",  border:"rgba(52,211,153,0.35)" },
  dev:    { label:"Dev",    color:"var(--badge-violet)", bg:"rgba(124,58,237,0.15)",  border:"rgba(124,58,237,0.35)" },
  closed: { label:"Closed", color:"var(--badge-gray)",   bg:"rgba(148,163,184,0.12)", border:"rgba(148,163,184,0.25)" },
};

const StatusPill = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.bot;
  return (
    <span style={{ ...sx.badgeBase, background:s.bg, color:s.color, border:`1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
};

const TABS = [
  { key:"live",   label:"Live" },
  { key:"needs",  label:"Needs agent" },
  { key:"closed", label:"Closed" },
  { key:"all",    label:"All" },
];

const matchesTab = (s, tab) => {
  if (tab === "all")    return true;
  if (tab === "closed") return s.status === "closed";
  // "Needs agent": a human is on it or the visitor is being handed off — not the bot.
  if (tab === "needs")  return ["intake", "agent", "dev"].includes(s.status);
  return s.status !== "closed"; // "live"
};

// Open first, then most-recent activity first.
const bySession = (a, b) => {
  const ao = a.status === "closed" ? 1 : 0, bo = b.status === "closed" ? 1 : 0;
  if (ao !== bo) return ao - bo;
  return new Date(b.last_user_activity_at || b.created_at) - new Date(a.last_user_activity_at || a.created_at);
};

export default function SupportChats() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { agent } = useAuth();
  const isAdmin  = agent?.role === "admin";

  const [view, setView] = useState("console"); // "console" | "sites"

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden", background:"var(--t-page)" }}>
      {/* Top view switch — only admins get the Sites tab */}
      {isAdmin && (
        <div style={{ display:"flex", gap:6, padding:"10px 16px 0", borderBottom:"1px solid var(--t-border-soft)", flexShrink:0, background:"var(--t-grad-list-head)" }}>
          {[{ key:"console", label:"Console", icon:MessageSquare }, { key:"sites", label:"Sites", icon:KeyRound }].map(t => (
            <button key={t.key} onClick={() => setView(t.key)} style={{
              display:"inline-flex", alignItems:"center", gap:7, padding:"9px 14px",
              background:"none", border:"none", borderBottom: view===t.key ? `2px solid ${tokens.accent.indigo}` : "2px solid transparent",
              color: view===t.key ? "var(--t-text-primary)" : "var(--t-text-faint)",
              fontSize:13, fontWeight: view===t.key ? 700 : 500, cursor:"pointer", fontFamily:"inherit",
            }}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      )}

      {view === "sites" && isAdmin ? <SitesPanel /> : <Console isMobile={isMobile} agent={agent} navigate={navigate} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Console — session list + transcript
// ═══════════════════════════════════════════════════════════════════════════
function Console({ isMobile, agent, navigate }) {
  const [sessions,  setSessions]  = useState([]);
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [listErr,   setListErr]   = useState("");
  const [tab,       setTab]       = useState("live");
  const [selectedId, setSelectedId] = useState(null);

  const [messages,  setMessages]  = useState([]);
  const [msgInput,  setMsgInput]  = useState("");
  const [sending,   setSending]   = useState(false);
  const [actionErr, setActionErr] = useState("");
  const [acting,    setActing]    = useState(false);
  const [metaOpen,  setMetaOpen]  = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);

  const sinceRef       = useRef(0);
  const seenRef        = useRef(null);   // id -> message_count last observed (unread tracking)
  const messagesEndRef = useRef(null);

  const selected = sessions.find(s => s.id === selectedId) || null;
  const isClosed = selected?.status === "closed";

  // ── Session list (poll 10s) ───────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    try {
      const res  = await getChatSessions();
      const list = res.data.sessions || [];
      // First load: everything currently visible counts as "seen" so the console
      // doesn't open with every session flagged unread.
      if (seenRef.current === null) {
        seenRef.current = {};
        list.forEach(s => { seenRef.current[s.id] = s.message_count; });
      }
      setSessions(list);
      setListErr("");
    } catch (e) { setListErr(errText(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadSessions();
    const i = setInterval(loadSessions, 10000);
    return () => clearInterval(i);
  }, [loadSessions]);

  // ── Stats strip (poll 10s) ────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    try { setStats((await getChatStats()).data); } catch { /* header is best-effort */ }
  }, []);
  useEffect(() => {
    loadStats();
    const i = setInterval(loadStats, 10000);
    return () => clearInterval(i);
  }, [loadStats]);

  // ── Transcript: append-only poll every 3s using the message-id cursor ──────
  const loadMessages = useCallback(async (id, reset) => {
    if (!id) return;
    try {
      const since = reset ? 0 : sinceRef.current;
      const rows  = (await getChatMessages(id, since)).data.messages || [];
      if (!rows.length) return;
      sinceRef.current = rows[rows.length - 1].id;
      setMessages(prev => reset ? rows : [...prev, ...rows]);
    } catch { /* transient poll errors are non-fatal */ }
  }, []);

  useEffect(() => {
    if (!selectedId) { setMessages([]); sinceRef.current = 0; return; }
    sinceRef.current = 0;
    setMessages([]);
    setMetaOpen(false);
    setActionErr("");
    loadMessages(selectedId, true);
    const i = setInterval(() => loadMessages(selectedId, false), 3000);
    return () => clearInterval(i);
  }, [selectedId, loadMessages]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  // Keep the open session marked read as new messages arrive for it.
  useEffect(() => {
    if (selectedId && selected && seenRef.current) seenRef.current[selectedId] = selected.message_count;
  }, [selectedId, selected]);

  const openSession = (s) => {
    if (seenRef.current) seenRef.current[s.id] = s.message_count;
    setSelectedId(prev => prev === s.id ? null : s.id);
  };

  const isUnread = (s) =>
    seenRef.current && s.id !== selectedId && s.message_count > (seenRef.current[s.id] ?? 0);

  // ── Actions ────────────────────────────────────────────────────────────────
  const runAction = async (fn) => {
    setActionErr("");
    setActing(true);
    try {
      await fn();
      await loadSessions();
      await loadMessages(selectedId, false);
    } catch (e) { setActionErr(errText(e)); }
    finally { setActing(false); }
  };

  const sendMessage = async () => {
    if (!msgInput.trim() || !selectedId || sending) return;
    setSending(true);
    setActionErr("");
    try {
      const res = await sendChatMessage(selectedId, msgInput.trim());
      // Append the returned row and advance the cursor so the 3s poll won't dup it.
      const m = res.data.message;
      if (m) { setMessages(prev => [...prev, m]); sinceRef.current = m.id; }
      setMsgInput("");
      loadSessions(); // sending auto-takes-over server-side — reflect the status change
    } catch (e) { setActionErr(errText(e)); }
    finally { setSending(false); }
  };

  const doEscalate = async (data) => {
    await escalateChat(selectedId, data); // throws on 409 (already escalated) → surfaced by caller
    setShowEscalate(false);
    await loadSessions();
    await loadMessages(selectedId, false);
  };

  const visible = sessions.filter(s => matchesTab(s, tab)).sort(bySession);

  const statCards = stats ? [
    { label:"With bot",      value:stats.with_bot,      color:tokens.accent.blue },
    { label:"In intake",     value:stats.in_intake,     color:tokens.accent.amber },
    { label:"With agent",    value:stats.with_agent,    color:tokens.accent.green },
    { label:"Started 24h",   value:stats.started_24h,   color:tokens.accent.violet },
    { label:"Escalated 24h", value:stats.escalated_24h, color:tokens.accent.orange },
  ] : [];

  const tokUsed = Number(stats?.ai?.tokens_24h || 0);
  const tokCap  = Number(stats?.ai?.daily_token_cap || 0);
  const tokPct  = tokCap ? Math.min(100, Math.round((tokUsed / tokCap) * 100)) : 0;

  return (
    <div style={{ display:"flex", flex:1, minHeight:0, overflow:"hidden" }}>

      {/* ── Left: stats + filters + list ───────────────────────────────────── */}
      <div style={{
        width: isMobile ? "100%" : (selectedId ? 380 : "100%"), flexShrink:0,
        display: isMobile && selectedId ? "none" : "flex", flexDirection:"column",
        borderRight: isMobile ? "none" : "1px solid var(--t-border-soft)", overflow:"hidden",
      }}>

        {/* Head + stats */}
        <div style={{ padding:"16px 16px 12px", borderBottom:"1px solid var(--t-border-soft)", flexShrink:0, background:"var(--t-grad-list-head)" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
            <div>
              <h1 style={{ margin:0, fontSize:18, fontWeight:800, color:"var(--t-text-primary)", letterSpacing:"-0.3px", display:"flex", alignItems:"center", gap:8 }}>
                <MessageSquare size={17} /> Support Chats
              </h1>
              <p style={{ margin:"2px 0 0", color:"var(--t-text-faint)", fontSize:12 }}>Live chat console — MIDTC Chats</p>
            </div>
            <button onClick={() => { loadSessions(); loadStats(); }} title="Refresh" style={{
              padding:"7px 10px", background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.14)",
              borderRadius:8, color:"var(--t-text-muted)", cursor:"pointer", display:"inline-flex", flexShrink:0,
            }}>
              <RefreshCw size={13} />
            </button>
          </div>

          {/* Stat cards */}
          {stats && (
            <div style={{ display:"flex", gap:7, marginTop:12, flexWrap:"wrap" }}>
              {statCards.map(s => (
                <div key={s.label} style={{
                  flex:"1 1 60px", minWidth:60, background:"var(--t-panel)", border:"1px solid rgba(148,163,184,0.14)",
                  borderRadius:8, padding:"8px 10px", boxShadow:"inset 0 1px 0 rgba(255,255,255,0.04)",
                }}>
                  <div style={{ fontSize:19, fontWeight:800, letterSpacing:"-0.5px", lineHeight:1, color:s.color }}>{s.value ?? 0}</div>
                  <div style={{ fontSize:9, color:"var(--t-text-faint)", marginTop:3, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em" }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* AI token budget */}
          {stats?.ai && (
            <div style={{ marginTop:10, background:"var(--t-panel)", border:"1px solid rgba(148,163,184,0.14)", borderRadius:8, padding:"9px 11px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", fontSize:11, marginBottom:6 }}>
                <span style={{ color:"var(--t-text-faint)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", display:"inline-flex", alignItems:"center", gap:5 }}>
                  <Bot size={12} /> AI tokens · 24h
                </span>
                <span style={{ color: tokPct >= 90 ? "var(--badge-red)" : "var(--t-text-muted)", fontWeight:700, fontFamily:"'SF Mono','Fira Code',monospace" }}>
                  {tokUsed.toLocaleString()} / {tokCap.toLocaleString()}
                </span>
              </div>
              <div style={{ height:6, borderRadius:99, background:"rgba(148,163,184,0.15)", overflow:"hidden" }}>
                <div style={{ width:`${tokPct}%`, height:"100%", borderRadius:99,
                  background: tokPct >= 90 ? `linear-gradient(90deg,${tokens.accent.red2},${tokens.accent.red})` : tokens.gradient.primary,
                  transition:"width 0.3s" }} />
              </div>
              <div style={{ marginTop:5, fontSize:10, color:"var(--t-text-faint)" }}>
                {stats.ai.runs_24h} runs · {Number(stats.ai.cache_read_24h || 0).toLocaleString()} cached · {stats.ai.model}
              </div>
            </div>
          )}
        </div>

        {/* Filter tabs */}
        <div style={{ display:"flex", gap:6, padding:"9px 14px", borderBottom:"1px solid var(--t-border-soft)", flexShrink:0, background:"var(--t-panel)", overflowX:"auto" }}>
          {TABS.map(t => {
            const active = tab === t.key;
            const count  = sessions.filter(s => matchesTab(s, t.key)).length;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                display:"inline-flex", alignItems:"center", gap:6, padding:"6px 11px", borderRadius:8, whiteSpace:"nowrap",
                background: active ? "rgba(79,111,255,0.15)" : "var(--t-input)",
                border: active ? "1px solid rgba(79,111,255,0.35)" : "1px solid rgba(148,163,184,0.14)",
                color: active ? "#818cf8" : "var(--t-text-muted)", fontSize:12, fontWeight: active ? 700 : 500,
                cursor:"pointer", fontFamily:"inherit",
              }}>
                {t.label}
                <span style={{ fontSize:10, opacity:0.8, fontWeight:700 }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* List */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {loading && <div style={{ padding:32, textAlign:"center", color:"var(--t-text-faint)" }}>Loading…</div>}
          {!loading && listErr && (
            <div style={{ margin:16, background:"rgba(220,38,38,0.12)", border:"1px solid rgba(220,38,38,0.35)", color: tokens.accent.red2, borderRadius:8, padding:"10px 12px", fontSize:12 }}>
              <AlertTriangle size={13} style={{ verticalAlign:"-2px", marginRight:6 }} />{listErr}
            </div>
          )}
          {!loading && !listErr && visible.length === 0 && (
            <div style={{ padding:48, textAlign:"center", color:"var(--t-text-faint)" }}>
              <div style={{ fontSize:38, marginBottom:10 }}>💬</div>
              <div style={{ fontSize:15, fontWeight:600 }}>No chats here</div>
              <div style={{ fontSize:13, marginTop:4 }}>Nothing matches the “{TABS.find(t => t.key === tab)?.label}” filter.</div>
            </div>
          )}
          {visible.map(s => {
            const isSel = selectedId === s.id;
            const who   = s.contact_name || s.wp_user_name || "Anonymous visitor";
            return (
              <div key={s.id} onClick={() => openSession(s)} style={{
                padding:"12px 16px", borderBottom:"1px solid var(--t-border-soft)", cursor:"pointer",
                background: isSel ? "linear-gradient(90deg,rgba(124,58,237,0.12),rgba(124,58,237,0.03))" : "transparent",
                borderLeft: isSel ? `3px solid ${tokens.accent.indigo}` : "3px solid transparent", transition:"all 0.12s",
              }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "rgba(99,102,241,0.04)"; }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:5 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7, minWidth:0 }}>
                    {isUnread(s) && <span style={{ width:8, height:8, borderRadius:"50%", background: tokens.accent.indigo, boxShadow: `0 0 6px ${tokens.accent.indigo}`, flexShrink:0 }} />}
                    <span style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{who}</span>
                  </div>
                  <div style={{ display:"flex", gap:5, alignItems:"center", flexShrink:0 }}>
                    {s.ticket_number != null && (
                      <span style={{ display:"inline-flex", alignItems:"center", gap:3, fontSize:10, color: tokens.accent.blue2, fontFamily:"'SF Mono','Fira Code',monospace" }}>
                        <TicketIcon size={10} /> {s.ticket_number}
                      </span>
                    )}
                    <StatusPill status={s.status} />
                  </div>
                </div>
                <div style={{ fontSize:11, color:"var(--t-text-muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:4 }}>
                  {s.last_user_message || <span style={{ color:"var(--t-text-faint)", fontStyle:"italic" }}>No visitor message yet</span>}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:10.5, color:"var(--t-text-faint)", flexWrap:"wrap" }}>
                  <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:150 }}>{s.site_name}</span>
                  <span>·</span>
                  <span>{fmtRel(s.last_user_activity_at || s.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Right: transcript ──────────────────────────────────────────────── */}
      {selected && (
        <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", overflow:"hidden", background:"var(--t-page)" }}>

          {/* Detail head + actions */}
          <div style={{ padding:"14px 18px 12px", borderBottom:"1px solid var(--t-border-soft)", flexShrink:0, background:"var(--t-grad-list-head)" }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:"var(--t-text-primary)", letterSpacing:"-0.3px" }}>
                    {selected.contact_name || selected.wp_user_name || "Anonymous visitor"}
                  </h2>
                  <StatusPill status={selected.status} />
                  {selected.ticket_number != null && (
                    <button onClick={() => selected.ticket_id && navigate(`/tickets/${selected.ticket_id}`)}
                      title="Open the linked ticket"
                      style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, color: tokens.accent.blue2,
                        fontFamily:"'SF Mono','Fira Code',monospace", background:"rgba(96,165,250,0.1)",
                        border:"1px solid rgba(96,165,250,0.3)", borderRadius:6, padding:"2px 8px", cursor:"pointer" }}>
                      <TicketIcon size={11} /> {selected.ticket_number}
                    </button>
                  )}
                </div>
                <div style={{ marginTop:4, fontSize:12, color:"var(--t-text-faint)" }}>
                  {selected.site_name}{selected.contact_email ? ` · ${selected.contact_email}` : ""}
                </div>
              </div>
              <button onClick={() => setSelectedId(null)} style={{ background:"none", border:"none", color:"var(--t-text-faint)", fontSize:19, cursor:"pointer", padding:"2px 6px", lineHeight:1 }}>×</button>
            </div>

            {/* Action row */}
            <div style={{ display:"flex", gap:7, marginTop:11, flexWrap:"wrap" }}>
              {!isClosed && selected.bot_active && (
                <ActionBtn onClick={() => runAction(() => takeoverChat(selectedId))} disabled={acting} icon={Headphones}>Take over</ActionBtn>
              )}
              {!isClosed && selected.status === "agent" && (
                <ActionBtn onClick={() => runAction(() => releaseChat(selectedId))} disabled={acting} icon={Bot}>Hand back to bot</ActionBtn>
              )}
              {!isClosed && !selected.ticket_id && (
                <ActionBtn onClick={() => setShowEscalate(true)} disabled={acting} icon={AlertTriangle} tone="warn">Escalate</ActionBtn>
              )}
              {!isClosed && (
                <ActionBtn onClick={() => runAction(() => closeChat(selectedId))} disabled={acting} icon={X} tone="danger">Close chat</ActionBtn>
              )}
              {selected.ticket_id && (
                <ActionBtn onClick={() => navigate(`/tickets/${selected.ticket_id}`)} icon={TicketIcon}>View ticket</ActionBtn>
              )}
            </div>

            {actionErr && (
              <div style={{ marginTop:9, background:"rgba(220,38,38,0.12)", border:"1px solid rgba(220,38,38,0.35)", color: tokens.accent.red2, borderRadius:8, padding:"7px 11px", fontSize:12 }}>
                <AlertTriangle size={12} style={{ verticalAlign:"-2px", marginRight:6 }} />{actionErr}
              </div>
            )}

            {/* Collapsible visitor / site metadata */}
            <button onClick={() => setMetaOpen(o => !o)} style={{
              marginTop:10, display:"inline-flex", alignItems:"center", gap:5, background:"none", border:"none",
              color:"var(--t-text-faint)", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", padding:0,
            }}>
              {metaOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Visitor & site details
            </button>
            {metaOpen && (
              <div style={{ marginTop:8, display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))", gap:"6px 14px",
                background:"var(--t-panel)", border:"1px solid rgba(148,163,184,0.14)", borderRadius:8, padding:"10px 12px" }}>
                {[
                  ["Name",       selected.contact_name || selected.wp_user_name],
                  ["Email",      selected.contact_email || selected.wp_user_email],
                  ["Phone",      selected.contact_phone],
                  ["Site",       selected.site_name],
                  ["Site URL",   selected.site_url],
                  ["Page",       selected.page_url],
                  ["WordPress",  selected.wp_version],
                  ["WP user",    selected.wp_user_name],
                  ["Started",    fmtTime(selected.created_at)],
                  ["Escalated",  selected.escalated_at ? fmtTime(selected.escalated_at) : null],
                  ["Closed",     selected.closed_at ? `${fmtTime(selected.closed_at)}${selected.close_reason ? ` (${selected.close_reason})` : ""}` : null],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{ minWidth:0 }}>
                    <div style={{ fontSize:9.5, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", color:"var(--t-text-faint)" }}>{k}</div>
                    <div style={{ fontSize:12, color:"var(--t-text-body)", wordBreak:"break-word" }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Transcript */}
          <div style={{ flex:1, minHeight:0, overflowY:"auto", padding:"14px 18px", display:"flex", flexDirection:"column", gap:10 }}>
            {messages.length === 0 && (
              <div style={{ margin:"auto", textAlign:"center", color:"var(--t-text-faint)", fontSize:13 }}>No messages in this chat yet.</div>
            )}
            {messages.map(m => <Bubble key={m.id} m={m} />)}
            <div ref={messagesEndRef} />
          </div>

          {/* Composer */}
          <div style={{ flexShrink:0, padding:"10px 16px", borderTop:"1px solid var(--t-border-soft)", background:"var(--t-panel)" }}>
            {isClosed ? (
              <div style={{ textAlign:"center", color:"var(--t-text-faint)", fontSize:12, padding:"8px 0" }}>
                This chat is closed{selected.close_reason ? ` (${selected.close_reason})` : ""} — you can’t send new messages.
              </div>
            ) : (
              <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
                <textarea
                  value={msgInput}
                  onChange={e => setMsgInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Reply as an agent… (Enter to send, Shift+Enter for a new line)"
                  rows={2}
                  style={{ flex:1, minWidth:0, background:"var(--t-card-input)", border:"1px solid rgba(99,162,255,0.3)", borderRadius:8, padding:"9px 12px", color:"var(--t-text-primary)", fontSize:13, resize:"none", boxSizing:"border-box", fontFamily:"inherit", lineHeight:1.5 }}
                />
                <button onClick={sendMessage} disabled={sending || !msgInput.trim()} style={{
                  ...sx.buttonPrimary, padding:"10px 16px",
                  cursor: sending || !msgInput.trim() ? "not-allowed" : "pointer",
                  opacity: sending || !msgInput.trim() ? 0.5 : 1, flexShrink:0,
                }}>
                  <Send size={14} /> {sending ? "…" : "Send"}
                </button>
              </div>
            )}
            {!isClosed && selected.bot_active && (
              <div style={{ marginTop:6, fontSize:10.5, color:"var(--t-text-faint)" }}>
                The assistant is handling this chat — sending a message takes it over.
              </div>
            )}
          </div>
        </div>
      )}

      {showEscalate && (
        <EscalateModal
          onClose={() => setShowEscalate(false)}
          onSubmit={doEscalate}
        />
      )}
    </div>
  );
}

// ── Message bubble ───────────────────────────────────────────────────────────
function Bubble({ m }) {
  if (m.sender === "system") {
    return (
      <div style={{ alignSelf:"center", maxWidth:"80%", textAlign:"center", fontSize:11, color:"var(--t-text-faint)",
        background:"rgba(148,163,184,0.1)", border:"1px solid rgba(148,163,184,0.16)", borderRadius:99, padding:"4px 12px" }}>
        {m.body} · {fmtTime(m.created_at)}
      </div>
    );
  }

  // Visitor on the left; bot + agent (MIDTC side) on the right.
  const isVisitor = m.sender === "user";
  const meta = {
    user:  { name:"Visitor",           icon:User,     color:"var(--badge-gray)" },
    bot:   { name:"MIDTC Assistant",   icon:Bot,      color:"var(--badge-blue)" },
    agent: { name:m.sender_name || "MIDTC Agent", icon:Headphones, color:"var(--badge-green)" },
  }[m.sender] || { name:m.sender_name || m.sender, icon:User, color:"var(--t-text-muted)" };
  const Icon = meta.icon;

  const bubbleStyle = isVisitor
    ? { background:"var(--t-input-hi)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:"12px 12px 12px 4px", color:"var(--t-text-body)" }
    : m.sender === "agent"
      ? { background: tokens.gradient.primary, border:"none", borderRadius:"12px 12px 4px 12px", color:"#fff", boxShadow: tokens.shadow.primaryGlow }
      : { background:"rgba(96,165,250,0.1)", border:"1px solid rgba(96,165,250,0.28)", borderRadius:"12px 12px 4px 12px", color:"var(--t-text-body)" };

  return (
    <div style={{ display:"flex", flexDirection: isVisitor ? "row" : "row-reverse", alignItems:"flex-end", gap:8, flexShrink:0 }}>
      <span style={{ width:26, height:26, borderRadius:"50%", background:"rgba(99,102,241,0.14)", color:meta.color, display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Icon size={13} />
      </span>
      <div style={{ display:"flex", flexDirection:"column", alignItems: isVisitor ? "flex-start" : "flex-end", maxWidth:"76%", minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3, flexDirection: isVisitor ? "row" : "row-reverse" }}>
          <span style={{ fontSize:11.5, fontWeight:600, color:meta.color }}>{meta.name}</span>
          <span style={{ fontSize:10, color:"var(--t-text-faint)" }}>{fmtTime(m.created_at)}</span>
        </div>
        <div style={{ padding:"8px 12px", fontSize:13, lineHeight:1.55, wordBreak:"break-word", whiteSpace:"pre-wrap", ...bubbleStyle }}>
          {m.body}
          {m.attachment_url && (
            <div style={{ marginTop: m.body ? 6 : 0 }}>
              <a href={`${ORIGIN}${m.attachment_url}`} target="_blank" rel="noopener noreferrer"
                style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color: m.sender === "agent" ? "#fff" : tokens.accent.blue2, textDecoration:"underline" }}>
                📎 {m.attachment_name || "Attachment"}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Reusable action button ───────────────────────────────────────────────────
function ActionBtn({ children, icon: Icon, onClick, disabled, tone }) {
  const base = tone === "danger" ? sx.buttonDanger : tone === "warn"
    ? { ...sx.buttonGhost, background:"rgba(234,179,8,0.1)", border:"1px solid rgba(234,179,8,0.3)", color:"var(--badge-amber)" }
    : sx.buttonGhost;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...base, padding:"7px 13px", fontSize:12,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
    }}>
      {Icon && <Icon size={13} />} {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Escalate modal
// ═══════════════════════════════════════════════════════════════════════════
function EscalateModal({ onClose, onSubmit }) {
  const [form, setForm] = useState({ subject:"", summary:"", route:"support", priority:"Medium", category:"General" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.subject.trim()) { setErr("A subject is required."); return; }
    setSaving(true);
    setErr("");
    try { await onSubmit({ ...form, subject: form.subject.trim(), summary: form.summary.trim() }); }
    catch (e) { setErr(errText(e)); setSaving(false); }
  };

  const fieldStyle = { ...sx.input, boxSizing:"border-box" };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(2,6,23,0.6)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...sx.card, width:480, maxWidth:"100%", maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ ...sx.cardHead, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span>Escalate chat to a ticket</span>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--t-text-faint)", cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:16, display:"flex", flexDirection:"column", gap:12 }}>
          <p style={{ margin:0, fontSize:12, color:"var(--t-text-faint)", lineHeight:1.5 }}>
            This forces intake (the visitor is asked for contact details) and opens a ticket with the full transcript.
          </p>
          <Field label="Subject *">
            <input style={fieldStyle} value={form.subject} onChange={e => set("subject", e.target.value)} placeholder="Short ticket subject" autoFocus />
          </Field>
          <Field label="Summary">
            <textarea style={{ ...fieldStyle, resize:"vertical", lineHeight:1.5 }} rows={3} value={form.summary} onChange={e => set("summary", e.target.value)} placeholder="What the visitor needs, and what you've established" />
          </Field>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            <Field label="Route" style={{ flex:"1 1 120px" }}>
              <select style={fieldStyle} value={form.route} onChange={e => set("route", e.target.value)}>
                <option value="support">Support</option>
                <option value="dev">Dev</option>
              </select>
            </Field>
            <Field label="Priority" style={{ flex:"1 1 120px" }}>
              <select style={fieldStyle} value={form.priority} onChange={e => set("priority", e.target.value)}>
                {["Critical", "High", "Medium", "Low"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Category">
            <input style={fieldStyle} value={form.category} onChange={e => set("category", e.target.value)} placeholder="e.g. Website, Email, Billing" />
          </Field>
          {err && (
            <div style={{ background:"rgba(220,38,38,0.12)", border:"1px solid rgba(220,38,38,0.35)", color: tokens.accent.red2, borderRadius:8, padding:"8px 11px", fontSize:12 }}>
              <AlertTriangle size={12} style={{ verticalAlign:"-2px", marginRight:6 }} />{err}
            </div>
          )}
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button onClick={onClose} style={{ ...sx.buttonGhost, padding:"9px 15px" }}>Cancel</button>
            <button onClick={submit} disabled={saving} style={{ ...sx.buttonPrimary, padding:"9px 16px", opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Escalating…" : "Escalate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children, style }) => (
  <div style={{ display:"flex", flexDirection:"column", gap:5, ...style }}>
    <label style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--t-text-faint)" }}>{label}</label>
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
//  Sites panel (admin only)
// ═══════════════════════════════════════════════════════════════════════════
function SitesPanel() {
  const [sites,   setSites]   = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");
  const [showForm, setShowForm] = useState(false);
  const [revealKey, setRevealKey] = useState(null); // { name, site_key } shown once
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSites((await getChatSites()).data.sites || []); setErr(""); }
    catch (e) { setErr(errText(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getClients().then(r => setClients(r.data || [])).catch(() => {}); }, []);

  const copyKey = (k) => {
    navigator.clipboard?.writeText(k).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {});
  };

  const rotate = async (id) => {
    setErr("");
    try {
      const r = await rotateChatSiteKey(id);
      setRevealKey({ name: r.data.site?.name || "site", site_key: r.data.site_key });
      await load();
    } catch (e) { setErr(errText(e)); }
  };

  const toggleActive = async (site) => {
    setErr("");
    try { await updateChatSite(site.id, { active: !site.active }); await load(); }
    catch (e) { setErr(errText(e)); }
  };

  return (
    <div style={{ flex:1, minHeight:0, overflowY:"auto", padding:20 }}>
      <div style={{ maxWidth:1000, margin:"0 auto" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom:16, flexWrap:"wrap" }}>
          <div>
            <h1 style={{ margin:0, fontSize:20, fontWeight:800, color:"var(--t-text-primary)", letterSpacing:"-0.3px" }}>Chat Sites</h1>
            <p style={{ margin:"3px 0 0", fontSize:13, color:"var(--t-text-faint)" }}>Client websites allowed to run the MIDTC Chats widget.</p>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            {/* Plain link, not a fetch+blob: the zip is public and unauthenticated,
                so the browser streams it with a real filename and a progress bar. */}
            <a
              href={`${ORIGIN}/downloads/chat-plugin`}
              title="WordPress plugin zip to install on the client's site"
              style={{ ...sx.buttonGhost, textDecoration:"none" }}
            >
              <Download size={15} /> Download plugin
            </a>
            <button onClick={() => setShowForm(f => !f)} style={{ ...sx.buttonPrimary }}>
              <Plus size={15} /> New site
            </button>
          </div>
        </div>

        {err && (
          <div style={{ marginBottom:14, background:"rgba(220,38,38,0.12)", border:"1px solid rgba(220,38,38,0.35)", color: tokens.accent.red2, borderRadius:8, padding:"10px 12px", fontSize:12 }}>
            <AlertTriangle size={13} style={{ verticalAlign:"-2px", marginRight:6 }} />{err}
          </div>
        )}

        {/* One-time key reveal */}
        {revealKey && (
          <div style={{ marginBottom:16, background:"rgba(52,211,153,0.08)", border:"1px solid rgba(52,211,153,0.35)", borderRadius:10, padding:16 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"var(--badge-green)", display:"inline-flex", alignItems:"center", gap:7 }}>
                <KeyRound size={15} /> Site key for {revealKey.name}
              </div>
              <button onClick={() => setRevealKey(null)} style={{ background:"none", border:"none", color:"var(--t-text-faint)", cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
            </div>
            <p style={{ margin:"7px 0 10px", fontSize:12, color: tokens.accent.red2, fontWeight:600 }}>
              <AlertTriangle size={12} style={{ verticalAlign:"-2px", marginRight:5 }} />
              Copy this now — it is shown only once and cannot be retrieved again.
            </p>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <code style={{ flex:1, minWidth:0, background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.2)", borderRadius:8, padding:"10px 12px", fontSize:12.5, color:"var(--t-text-primary)", fontFamily:"'SF Mono','Fira Code',monospace", wordBreak:"break-all" }}>
                {revealKey.site_key}
              </code>
              <button onClick={() => copyKey(revealKey.site_key)} style={{ ...sx.buttonGhost, padding:"9px 13px", flexShrink:0 }}>
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}

        {showForm && <SiteForm clients={clients} onCancel={() => setShowForm(false)} onCreated={(rk) => { setShowForm(false); setRevealKey(rk); load(); }} />}

        {/* Sites table */}
        <div style={{ ...sx.card, overflow:"hidden" }}>
          <div style={{ ...sx.cardHead }}>Registered sites ({sites.length})</div>
          {loading ? (
            <div style={{ padding:28, textAlign:"center", color:"var(--t-text-faint)" }}>Loading…</div>
          ) : sites.length === 0 ? (
            <div style={{ padding:36, textAlign:"center", color:"var(--t-text-faint)" }}>No sites yet. Create one to issue a widget key.</div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
                <thead>
                  <tr style={{ textAlign:"left", color:"var(--t-text-faint)", fontSize:10, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                    {["Site", "Origin", "Key", "Sessions", "Uses", "Status", ""].map(h => (
                      <th key={h} style={{ padding:"10px 14px", borderBottom:"1px solid var(--t-border-soft)", fontWeight:700, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sites.map(s => (
                    <tr key={s.id} style={{ borderBottom:"1px solid var(--t-border-soft)" }}>
                      <td style={{ padding:"11px 14px" }}>
                        <div style={{ fontWeight:600, color:"var(--t-text-primary)" }}>
                          {s.name} {s.is_test && <span style={{ ...sx.badgeBase, background:"rgba(234,179,8,0.15)", color:"var(--badge-amber)", border:"1px solid rgba(234,179,8,0.3)", marginLeft:4 }}>TEST</span>}
                        </div>
                        <a href={s.site_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color: tokens.accent.blue2, textDecoration:"none" }}>{s.site_url}</a>
                      </td>
                      <td style={{ padding:"11px 14px", color:"var(--t-text-muted)", fontFamily:"'SF Mono','Fira Code',monospace", fontSize:11 }}>{s.allowed_origin}</td>
                      <td style={{ padding:"11px 14px", color:"var(--t-text-faint)", fontFamily:"'SF Mono','Fira Code',monospace", fontSize:11 }}>{s.site_key_partial}</td>
                      <td style={{ padding:"11px 14px", color:"var(--t-text-body)", whiteSpace:"nowrap" }}>
                        <span style={{ color:"var(--badge-green)", fontWeight:700 }}>{s.sessions_open}</span> open / {s.sessions_total}
                      </td>
                      <td style={{ padding:"11px 14px", color:"var(--t-text-muted)" }}>{s.use_count}</td>
                      <td style={{ padding:"11px 14px" }}>
                        <button onClick={() => toggleActive(s)} title="Toggle active" style={{
                          ...sx.badgeBase, cursor:"pointer",
                          background: s.active ? "rgba(52,211,153,0.15)" : "rgba(148,163,184,0.12)",
                          color: s.active ? "var(--badge-green)" : "var(--badge-gray)",
                          border: `1px solid ${s.active ? "rgba(52,211,153,0.35)" : "rgba(148,163,184,0.25)"}`,
                        }}>
                          {s.active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td style={{ padding:"11px 14px", whiteSpace:"nowrap" }}>
                        <button onClick={() => rotate(s.id)} title="Rotate key" style={{ ...sx.buttonGhost, padding:"6px 10px", fontSize:11 }}>
                          <RefreshCw size={12} /> Rotate key
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SiteForm({ clients, onCancel, onCreated }) {
  const [form, setForm] = useState({ name:"", site_url:"", allowed_origin:"", client_id:"", is_test:false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.site_url.trim() || !form.allowed_origin.trim()) {
      setErr("Name, site URL and allowed origin are all required."); return;
    }
    setSaving(true);
    setErr("");
    try {
      const r = await createChatSite({
        name: form.name.trim(), site_url: form.site_url.trim(),
        allowed_origin: form.allowed_origin.trim(),
        client_id: form.client_id || null, is_test: form.is_test,
      });
      onCreated({ name: r.data.site?.name || form.name.trim(), site_key: r.data.site_key });
    } catch (e) { setErr(errText(e)); setSaving(false); }
  };

  const fieldStyle = { ...sx.input, boxSizing:"border-box" };

  return (
    <div style={{ ...sx.card, marginBottom:16 }}>
      <div style={{ ...sx.cardHead }}>New chat site</div>
      <div style={{ padding:16, display:"flex", flexDirection:"column", gap:12 }}>
        <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
          <Field label="Name *" style={{ flex:"1 1 200px" }}>
            <input style={fieldStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="Acme Corp website" autoFocus />
          </Field>
          <Field label="Client (optional)" style={{ flex:"1 1 200px" }}>
            <select style={fieldStyle} value={form.client_id} onChange={e => set("client_id", e.target.value)}>
              <option value="">— None —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Site URL *">
          <input style={fieldStyle} value={form.site_url} onChange={e => set("site_url", e.target.value)} placeholder="https://acme.com" />
        </Field>
        <Field label="Allowed origin *">
          <input style={fieldStyle} value={form.allowed_origin} onChange={e => set("allowed_origin", e.target.value)} placeholder="https://acme.com (scheme + host only, no path or trailing slash)" />
        </Field>
        <label style={{ display:"inline-flex", alignItems:"center", gap:8, fontSize:12.5, color:"var(--t-text-body)", cursor:"pointer" }}>
          <input type="checkbox" checked={form.is_test} onChange={e => set("is_test", e.target.checked)} />
          Test site (won’t open real tickets in a way you’d want counted)
        </label>
        {err && (
          <div style={{ background:"rgba(220,38,38,0.12)", border:"1px solid rgba(220,38,38,0.35)", color: tokens.accent.red2, borderRadius:8, padding:"8px 11px", fontSize:12 }}>
            <AlertTriangle size={12} style={{ verticalAlign:"-2px", marginRight:6 }} />{err}
          </div>
        )}
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button onClick={onCancel} style={{ ...sx.buttonGhost, padding:"9px 15px" }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...sx.buttonPrimary, padding:"9px 16px", opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Creating…" : "Create site"}
          </button>
        </div>
      </div>
    </div>
  );
}
